/**
 * Platform discovery browse telemetry — public ingest (tenantId sentinel 0).
 * POST /api/v1/public/discovery/analytics-events
 *
 * Fail-closed when consent ingest gate is on and analytics consent is not granted.
 */
import {
  ANALYTICS_DISCOVERY_EVENTS,
  ANALYTICS_EVENT_CATALOG_VERSION,
} from 'pi-kiosk-shared/analyticsEvents';
import { logAnalyticsDevError } from 'pi-kiosk-shared/analyticsDevLog';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import type { CustomerAcquisitionSource } from './resolveCustomerAcquisitionSource.js';
import {
  saveDiscoveryAttributionContext,
} from './discoveryAttributionContext.js';

export type DiscoveryResultKind = 'open' | 'add' | 'shop';

export interface PublicDiscoveryTelemetryConsentGate {
  readonly consentIngestGateEnabled?: boolean;
  readonly analyticsConsentGranted?: boolean;
}

export interface EmitDiscoverySearchPerformedInput
  extends PublicDiscoveryTelemetryConsentGate {
  readonly apiBaseUrl: string;
  readonly searchRequestId: string;
  readonly resultCount: number;
  readonly source: CustomerAcquisitionSource;
  readonly entity?: string | null;
  readonly platformCategoryId?: string | null;
}

export interface EmitDiscoveryResultSelectedInput
  extends PublicDiscoveryTelemetryConsentGate {
  readonly apiBaseUrl: string;
  readonly searchRequestId: string;
  readonly resultKind: DiscoveryResultKind;
  readonly targetTenantCode: string;
  readonly targetTenantId: number;
  readonly targetProductId: number;
  readonly targetSalesPointId: number;
  readonly source: CustomerAcquisitionSource;
}

function trimBaseUrl(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, '');
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function isPublicDiscoveryTelemetryAllowed(
  gate: PublicDiscoveryTelemetryConsentGate,
): boolean {
  if (gate.consentIngestGateEnabled !== true) {
    return true;
  }
  return gate.analyticsConsentGranted === true;
}

async function postPublicDiscoveryTelemetryEvent(
  apiBaseUrl: string,
  input: {
    readonly eventName: string;
    readonly searchRequestId: string;
    readonly metadata: Record<string, unknown>;
  },
): Promise<void> {
  const base = trimBaseUrl(apiBaseUrl);
  const url = `${base}/api/v1/public/discovery/analytics-events`;
  const clientEventId = uuidv4();
  const body = {
    eventName: input.eventName,
    catalogVersion: ANALYTICS_EVENT_CATALOG_VERSION,
    clientEventId,
    sessionId: `pd:${input.searchRequestId}`,
    metadata: input.metadata,
  };
  const response = await stormAwareFetch(url, {
    method: 'POST',
    headers: platformJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`public_discovery_telemetry_http_${response.status}`);
  }
}

export async function emitDiscoverySearchPerformed(
  input: EmitDiscoverySearchPerformedInput,
): Promise<void> {
  if (!isPublicDiscoveryTelemetryAllowed(input)) {
    return;
  }
  try {
    await postPublicDiscoveryTelemetryEvent(input.apiBaseUrl, {
      eventName: ANALYTICS_DISCOVERY_EVENTS.DISCOVERY_SEARCH_PERFORMED,
      searchRequestId: input.searchRequestId,
      metadata: {
        search_request_id: input.searchRequestId,
        result_count: input.resultCount,
        source: input.source,
        ...(input.entity !== undefined && input.entity !== null
          ? { entity: input.entity }
          : {}),
        ...(input.platformCategoryId !== undefined &&
        input.platformCategoryId !== null &&
        input.platformCategoryId.trim().length > 0
          ? { platform_category_id: input.platformCategoryId.trim() }
          : {}),
      },
    });
  } catch (err) {
    logAnalyticsDevError('public_discovery_search_emit_failed', {
      reason: err instanceof Error ? err.message : String(err),
    });
  }
}

export async function emitDiscoveryResultSelected(
  input: EmitDiscoveryResultSelectedInput,
): Promise<void> {
  if (!isPublicDiscoveryTelemetryAllowed(input)) {
    return;
  }
  saveDiscoveryAttributionContext({
    searchRequestId: input.searchRequestId,
    source: input.source,
    targetTenantCode: input.targetTenantCode,
  });
  try {
    await postPublicDiscoveryTelemetryEvent(input.apiBaseUrl, {
      eventName: ANALYTICS_DISCOVERY_EVENTS.DISCOVERY_RESULT_SELECTED,
      searchRequestId: input.searchRequestId,
      metadata: {
        search_request_id: input.searchRequestId,
        result_kind: input.resultKind,
        target_tenant_id: input.targetTenantId,
        target_product_id: input.targetProductId,
        target_sales_point_id: input.targetSalesPointId,
      },
    });
  } catch (err) {
    logAnalyticsDevError('public_discovery_selection_emit_failed', {
      reason: err instanceof Error ? err.message : String(err),
    });
  }
}

/** Fire-and-forget wrapper for browse handlers. */
export function trackDiscoverySearchPerformed(
  input: EmitDiscoverySearchPerformedInput,
): void {
  void emitDiscoverySearchPerformed(input);
}

export function trackDiscoveryResultSelected(
  input: EmitDiscoveryResultSelectedInput,
): void {
  void emitDiscoveryResultSelected(input);
}
